import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { workerShiftAssignmentSchema, WorkerShiftAssignmentFormData } from "@/schemas/shiftSchemas";
import { useShifts, useCreateWorkerShiftAssignment } from "@/hooks/useShifts";
import { api } from "@/lib/apiClient";

interface WorkerAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedWorkerId?: string;
  preselectedShiftId?: string;
}

export function WorkerAssignmentDialog({
  open,
  onOpenChange,
  preselectedWorkerId,
  preselectedShiftId,
}: WorkerAssignmentDialogProps) {
  const { toast } = useToast();
  const { data: shifts } = useShifts(true);
  const createMutation = useCreateWorkerShiftAssignment();
  const isLoading = createMutation.isPending;

  // Fetch active workers
  const { data: workers } = useQuery({
    queryKey: ["workers-for-assignment"],
    queryFn: async () => {
      const { data, error } = await api
        .from("profiles")
        .select("id, full_name, employee_id, area")
        .eq("is_active", true)
        .order("full_name");
      if (error) throw error;
      return data as Array<{
        id: string;
        full_name: string;
        employee_id: string | null;
        area: string | null;
      }>;
    },
  });

  const form = useForm<WorkerShiftAssignmentFormData>({
    resolver: zodResolver(workerShiftAssignmentSchema),
    defaultValues: {
      worker_id: preselectedWorkerId || "",
      shift_id: preselectedShiftId || "",
      effective_date: new Date().toISOString().split("T")[0],
      end_date: null,
      notes: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        worker_id: preselectedWorkerId || "",
        shift_id: preselectedShiftId || "",
        effective_date: new Date().toISOString().split("T")[0],
        end_date: null,
        notes: "",
      });
    }
  }, [open, preselectedWorkerId, preselectedShiftId, form]);

  const onSubmit = async (data: WorkerShiftAssignmentFormData) => {
    try {
      await createMutation.mutateAsync(data);
      toast({
        title: "Asignación creada",
        description: "El trabajador ha sido asignado al turno correctamente",
      });
      onOpenChange(false);
      form.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al crear la asignación",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Asignar Trabajador a Turno</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="worker_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trabajador</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!!preselectedWorkerId}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar trabajador" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {workers?.map((worker) => (
                        <SelectItem key={worker.id} value={worker.id}>
                          {worker.full_name}
                          {worker.employee_id && ` (${worker.employee_id})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="shift_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Turno</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!!preselectedShiftId}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar turno" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {shifts?.map((shift) => (
                        <SelectItem key={shift.id} value={shift.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: shift.color }}
                            />
                            {shift.name} ({shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)})
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="effective_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha de inicio</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha de fin (opcional)</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas (opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Observaciones..."
                      className="resize-none"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Asignar
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
